import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Video,
  RefreshCw,
  XCircle,
  Eye,
  Calendar,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { MeetingDto } from "@shared/types/MeetingDto";
import { STATUS_LABELS, STATUS_COLORS, TYPE_LABELS } from "./constants";

interface MeetingsListProps {
  meetings: MeetingDto[];
  ghosted?: boolean;
  highlightMeetingId?: string | null;
  onView: (meeting: MeetingDto) => void;
  onReschedule: (meeting: MeetingDto) => void;
  onCancel: (meeting: MeetingDto) => void;
}

export function MeetingsList({
  meetings,
  ghosted = false,
  highlightMeetingId,
  onView,
  onReschedule,
  onCancel,
}: MeetingsListProps) {
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightMeetingId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightMeetingId]);

  if (meetings.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center py-16 text-center">
        <Calendar className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">No meetings found</p>
        <p className="text-sm text-muted-foreground/60 mt-1">
          Try adjusting your filters or create a new meeting.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {meetings.map((meeting, i) => {
        const scheduledDate = meeting.scheduledAt
          ? new Date(meeting.scheduledAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          : "Not scheduled";

        const statusColor =
          STATUS_COLORS[meeting.status] || "bg-muted text-muted-foreground";
        const canAct = !["CANCELLED", "COMPLETED", "NO_SHOW"].includes(
          meeting.status
        );

        const isHighlighted = highlightMeetingId === meeting.id;

        return (
          <motion.div
            key={meeting.id}
            ref={isHighlighted ? highlightRef : undefined}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: 1,
              y: 0,
              ...(isHighlighted
                ? { boxShadow: ["0 0 0 2px hsl(30 95% 55%)", "0 0 0 0px transparent"] }
                : {}),
            }}
            transition={{
              delay: i * 0.03,
              ...(isHighlighted ? { boxShadow: { duration: 1.5, delay: 0.3 } } : {}),
            }}
            className={isHighlighted ? "rounded-xl" : undefined}
          >
            <Card
              className={cn(
                "p-4 transition-colors",
                ghosted
                  ? "opacity-40 pointer-events-none select-none"
                  : "hover:bg-muted/30 cursor-pointer group",
                isHighlighted && "ring-2 ring-primary/60 animate-pulse [animation-duration:1.5s] [animation-iteration-count:2]"
              )}
              onClick={ghosted ? undefined : () => onView(meeting)}
              role={ghosted ? undefined : "button"}
              tabIndex={ghosted ? -1 : 0}
              aria-disabled={ghosted}
              aria-label={`Meeting: ${meeting.title} — ${scheduledDate} — ${STATUS_LABELS[meeting.status]}`}
              onKeyDown={
                ghosted
                  ? undefined
                  : (e) => {
                      if (e.key === "Enter") onView(meeting);
                    }
              }
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium truncate">{meeting.title}</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>
                      {meeting.displayName || meeting.email || "—"}
                    </span>
                    <span>·</span>
                    <span>{scheduledDate}</span>
                    {meeting.timezone && (
                      <>
                        <span>·</span>
                        <span className="text-xs">{meeting.timezone}</span>
                      </>
                    )}
                    <span>·</span>
                    <span>{meeting.durationMinutes} min</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-xs">
                    {TYPE_LABELS[meeting.type]}
                  </Badge>
                  <Badge className={`text-xs ${statusColor}`}>
                    {STATUS_LABELS[meeting.status]}
                  </Badge>

                  {!ghosted && (
                    <div
                      className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => onView(meeting)}
                        aria-label={`View meeting: ${meeting.title}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>

                      {meeting.meetingUrl && meeting.status === "CONFIRMED" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          asChild
                        >
                          <a
                            href={meeting.meetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Join meeting: ${meeting.title}`}
                          >
                            <Video className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      )}

                      {canAct && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => onReschedule(meeting)}
                            aria-label={`Reschedule meeting: ${meeting.title}`}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => onCancel(meeting)}
                            aria-label={`Cancel meeting: ${meeting.title}`}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}
